import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";

const STATUS_COLORS = {
  "Nuova richiesta": "bg-bronzo/15 text-bronzo",
  "Da contattare": "bg-amber-100 text-amber-800",
  "Intervento programmato": "bg-blue-100 text-blue-800",
  "Preventivo in preparazione": "bg-purple-100 text-purple-800",
  "Preventivo inviato": "bg-teal-100 text-teal-800",
  "Acquisita": "bg-green-100 text-green-800",
  "Non acquisita": "bg-ink/10 text-ink/50",
};

export const statusColor = (s) => STATUS_COLORS[s] || "bg-ink/10 text-ink/60";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div data-testid="dashboard-loading" />;

  const pipeline = [
    { key: "Nuova richiesta", label: "Nuove richieste", testid: "stat-nuove" },
    { key: "Da contattare", label: "Da contattare", testid: "stat-da-contattare" },
    { key: "Intervento programmato", label: "Interventi da organizzare", testid: "stat-interventi" },
    { key: "Preventivo in preparazione", label: "Preventivi da preparare", testid: "stat-preventivi" },
    { key: "Acquisita", label: "Richieste concluse (acquisite)", testid: "stat-concluse" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <Seo title="Dashboard | V32 Admin" noindex />
      <h1 className="font-display font-light text-3xl text-ink">Dashboard</h1>
      <p className="mt-2 text-sm text-ink/60">
        {stats.new_this_week > 0
          ? `${stats.new_this_week} nuov${stats.new_this_week === 1 ? "a richiesta" : "e richieste"} negli ultimi 7 giorni.`
          : "Nessuna nuova richiesta negli ultimi 7 giorni."}
      </p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {pipeline.map((p) => (
          <Link
            key={p.key}
            to={`/admin/richieste?status=${encodeURIComponent(p.key)}`}
            data-testid={p.testid}
            className="bg-white border border-ink/10 p-6 hover:border-bronzo hover:-translate-y-0.5 transition-[border-color,transform] duration-300"
          >
            <p className="font-display font-light text-4xl text-ink">{stats.by_status[p.key] || 0}</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">{p.label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-medium text-xl text-ink">Ultime richieste</h2>
          <Link to="/admin/richieste" data-testid="dashboard-all-leads" className="inline-flex items-center gap-2 text-sm text-bronzo hover:text-antracite transition-colors duration-300">
            Vedi tutte <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {stats.recent.length === 0 ? (
          <p className="bg-white border border-ink/10 p-8 text-sm text-ink/50" data-testid="dashboard-empty">
            Nessuna richiesta ancora registrata. Le richieste del modulo «Richiedi un sopralluogo» appariranno qui.
          </p>
        ) : (
          <div className="bg-white border border-ink/10 divide-y divide-ink/5" data-testid="dashboard-recent">
            {stats.recent.map((l) => (
              <Link
                key={l.id}
                to={`/admin/richieste?id=${l.id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 hover:bg-beige/50 transition-colors duration-200"
              >
                <div>
                  <p className="text-sm font-medium text-ink">{l.name}</p>
                  <p className="text-xs text-ink/50">{l.project_type} · {l.comune}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-[11px] font-medium px-2.5 py-1 ${statusColor(l.status)}`}>{l.status}</span>
                  <span className="text-xs text-ink/40 font-mono">
                    {new Date(l.created_at).toLocaleDateString("it-CH")}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
